import { Alert, Button, Container, Grid2, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ReactConfetti from 'react-confetti'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { graphql } from '../../gql'

interface IFormInput {
  orgName: string
}

const createOrgMutation = graphql(`
  mutation CreateOrg($name: String!) {
    CreateOrganization(name: $name) {
      name
      id
    }
  }
`)

type Props = {
  userGivenName?: string
  onCreated: (orgId: string) => void
}

export const CreateOrg: React.FC<Props> = (props) => {
  const [createOrgResult, createOrg] = useMutation(createOrgMutation)
  const [showConfetti, setShowConfetti] = useState(false)
  const [createdOrgId, setCreatedOrgId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (createdOrgId) {
      setShowConfetti(true)
      setTimeout(() => {
        setShowConfetti(false)
        props.onCreated(createdOrgId)
      }, 3000)
    }
  })

  const { handleSubmit, control, reset } = useForm<IFormInput>({
    defaultValues: {
      orgName: props.userGivenName ? `${props.userGivenName}'s Organization` : '',
    },
  })

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (createdOrgId) {
      return
    }
    await createOrg({
      name: data.orgName,
    }).then((r) => {
      if (!r.error) {
        if (r.data?.CreateOrganization?.id) {
          setCreatedOrgId(r.data?.CreateOrganization?.id)
        } else {
          console.error('No org id returned')
        }
      }
    })
  }

  return (
    <>
      <ReactConfetti
        numberOfPieces={showConfetti ? 500 : 0}
        recycle={false}
        initialVelocityY={3}
        initialVelocityX={3}
        gravity={0.2}
      />
      <Container maxWidth="sm" component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid2 container direction="column" spacing={2} alignItems="center" py={4}>
          <Typography variant="h2" gutterBottom>
            Create Your Organization
          </Typography>
          <Typography variant="body1" align="center">
            Your organization is where you and your team will collaborate. All your projects, commissions, etc are
            managed within your organization.
          </Typography>
          <Grid2 size={8}>
            <Controller
              name="orgName"
              control={control}
              rules={{
                required: true,
                maxLength: 255,
                minLength: { value: 3, message: 'Must be longer than 3 characters' },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  autoFocus
                  required
                  label="Organization Name"
                  fullWidth
                  variant="outlined"
                  {...field}
                  disabled={Boolean(createdOrgId) || createOrgResult.fetching}
                  error={!!fieldState.error}
                  helperText={fieldState.error && (fieldState.error?.message || 'Required')}
                />
              )}
            />
          </Grid2>
          {createOrgResult.error && (
            <Grid2 size={12}>
              <Alert severity="error">{createOrgResult.error.message}</Alert>
            </Grid2>
          )}
          <Grid2 size={8}>
            <Button
              variant="contained"
              fullWidth
              type="submit"
              loading={createOrgResult.fetching}
              disabled={createOrgResult.fetching || Boolean(createdOrgId)}
            >
              {createdOrgId ? 'Organization Created!' : 'Create Your Organization'}
            </Button>
          </Grid2>
        </Grid2>
      </Container>
    </>
  )
}
